/**
 * useRequestAdditionalFee — cleaner-side mutation that calls the
 * request-additional-fee Edge Function. Surfaces structured error
 * codes from the Edge Function + RPC as AppError.code so the inline
 * blocker UI can render them via the standard pattern (CLAUDE.md
 * ERROR 4).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export interface RequestAdditionalFeeVars {
  cleaningTaskId:        string
  requestedAmountCents:  number
  reason:                string
  photoStoragePaths?:    string[]
}

export interface RequestAdditionalFeeResult {
  request_id:             string
  status:                 'requested'
  cleaning_task_id:       string
  room_id:                string
  requested_amount_cents: number
  email_sent:             boolean
  notified_count:         number
}

export function useRequestAdditionalFee(): UseMutationResult<RequestAdditionalFeeResult, AppError, RequestAdditionalFeeVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<RequestAdditionalFeeResult, AppError, RequestAdditionalFeeVars>({
    mutationFn: async (vars): Promise<RequestAdditionalFeeResult> => {
      const { data, error } = await supabase.functions.invoke<RequestAdditionalFeeResult>('request-additional-fee', {
        body: {
          cleaning_task_id:       vars.cleaningTaskId,
          requested_amount_cents: vars.requestedAmountCents,
          reason:                 vars.reason,
          photo_storage_paths:    vars.photoStoragePaths,
        },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'REQUEST_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Request returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['additional-fee-requests']                    })
      void queryClient.invalidateQueries({ queryKey: ['additional-fee-requests', vars.cleaningTaskId] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_AMOUNT':       return 'Amount must be a whole number of cents between 0 and 1,000,000.'
    case 'INVALID_REASON':       return 'Provide a reason between 1 and 1,000 characters.'
    case 'INVALID_PHOTO_PATHS':  return 'Photo references must be non-empty strings under 500 characters.'
    case 'TOO_MANY_PHOTOS':      return 'At most 10 photos can be attached to a single fee request.'
    case 'FORBIDDEN_ROLE':       return 'Only cleaners can file an additional-fee request.'
    case 'FORBIDDEN_OWNERSHIP':  return 'You can only file a fee request for a task assigned to you.'
    case 'FORBIDDEN_ORG':        return 'This task does not belong to your organization.'
    case 'INVALID_STATE':        return 'This task is not in a state where a fee request can be filed.'
    case 'TASK_NOT_FOUND':       return 'This task could not be found.'
    case 'RATE_LIMITED':         return 'Too many requests in a row. Try again in a moment.'
    default:                     return detail || 'Could not file the fee request.'
  }
}
